import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useRef, useCallback, useEffect, useLayoutEffect } from 'react';
import { FaThumbtack, FaTimes } from 'react-icons/fa'; // Example icons for pinning and closing
import './FloatingPanel.scss';
// Function to convert viewport coords to canvas coords
const viewportToCanvasCoords = (viewportPos, zoom, translate) => {
    return {
        x: (viewportPos.x - translate.x) / zoom,
        y: (viewportPos.y - translate.y) / zoom,
    };
};
// Function to convert canvas coords to viewport coords
const canvasToViewportCoords = (canvasPos, zoom, translate) => {
    return {
        x: canvasPos.x * zoom + translate.x,
        y: canvasPos.y * zoom + translate.y,
    };
};
// Z-index layering. The editor toolbar paints at TOOLBAR_Z_INDEX; a panel that
// overlaps it must be raised above so it stays clickable. ELEVATED stays *below*
// body-level modals (ModalOverlay portals out of the editor's fixed layout, so
// it always wins regardless of this value — see ModalOverlay.tsx).
export const DEFAULT_PANEL_Z_INDEX = 100;
export const TOOLBAR_Z_INDEX = 9999;
export const ELEVATED_PANEL_Z_INDEX = 10000;
// Keep the panel fully on screen so its draggable header is always reachable.
// The header MAY overlap the toolbar (the panel is raised above the toolbar
// while it's there), but it can never leave the window on any side — that is
// what made a dragged-away panel impossible to grab back. Pure for testing.
export const clampPanelPosition = (pos, bounds) => {
    const maxX = Math.max(0, bounds.viewportWidth - bounds.panelWidth);
    const maxY = Math.max(0, bounds.viewportHeight - bounds.headerHeight);
    return {
        x: Math.min(Math.max(pos.x, 0), maxX),
        y: Math.min(Math.max(pos.y, 0), maxY),
    };
};
// Decide whether the panel should be raised above the toolbar. It must be on top
// while the user is dragging it and whenever it is parked over the toolbar band,
// so it can always be grabbed again. Pinned-to-canvas panels keep their own
// stacking. Pure for testing.
export const shouldElevatePanel = (state) => !state.isPinnedToCanvas && (state.isDragging || state.overlapsToolbar);
const FloatingPanel = ({ title, initialViewportPosition = { x: 50, y: 50 }, // Default viewport position
children, zoomLevel, canvasTranslate, parentViewportRef: _parentViewportRef, // Use the parent viewport ref
showPinButton = true, // Default to true
onClose, isDraggable = true, // <-- Default isDraggable to true
width, height, attachToViewport: _attachToViewport = true, // Default to attaching to viewport
zIndex, hideBackground = false, hideHeader = false, initialAboveToolbar = false }) => {
    // Store both coordinate types
    const [viewportPosition, setViewportPosition] = useState(initialViewportPosition);
    const [canvasPosition, setCanvasPosition] = useState(null); // Initially null
    const [isDragging, setIsDragging] = useState(false);
    const [, setDragStart] = useState({ clientX: 0, clientY: 0, elementX: 0, elementY: 0 }); // Store element's pos at drag start
    const [isPinnedToCanvas, setIsPinnedToCanvas] = useState(false);
    // Whether the panel currently overlaps the toolbar and so must be raised above
    // it to stay clickable.
    const [overlapsToolbar, setOverlapsToolbar] = useState(false);
    const panelRef = useRef(null);
    // Mirror of the latest viewport position so resize/mount handlers can re-clamp
    // without re-subscribing on every move.
    const viewportPositionRef = useRef(viewportPosition);
    // Initialize canvasPosition when component mounts if needed (e.g., load saved state)
    // For now, we'll calculate it when pinning
    // Calculate the effective screen position based on pin state
    const getEffectiveScreenPosition = useCallback(() => {
        if (isPinnedToCanvas && canvasPosition) {
            // Calculate screen position based on canvas position, zoom, and translate
            return canvasToViewportCoords(canvasPosition, zoomLevel, canvasTranslate);
        }
        // Otherwise, use the stored viewport position
        return viewportPosition;
    }, [isPinnedToCanvas, canvasPosition, viewportPosition, zoomLevel, canvasTranslate]);
    const effectiveScreenPosition = getEffectiveScreenPosition();
    // Measure the live viewport/panel, clamp a candidate viewport-space position
    // to the window (so the header can never leave the screen and become
    // un-grabbable), and report whether the clamped header overlaps the toolbar
    // band (so the caller can raise the panel above the toolbar). When
    // keepBelowToolbar is set the header is also pushed below the toolbar — used
    // for initial placement so default panels neither hide under the toolbar nor
    // sit elevated over it at rest.
    const clampAndMeasure = useCallback((pos, keepBelowToolbar = false) => {
        var _a, _b;
        const viewportEl = _parentViewportRef.current;
        if (!viewportEl)
            return { clamped: pos, overlapsToolbar: false };
        const viewportRect = viewportEl.getBoundingClientRect();
        const panelEl = panelRef.current;
        const panelWidth = (_a = panelEl === null || panelEl === void 0 ? void 0 : panelEl.offsetWidth) !== null && _a !== void 0 ? _a : (typeof width === 'number' ? width : 250);
        const headerEl = panelEl === null || panelEl === void 0 ? void 0 : panelEl.querySelector('.panel-header');
        const headerHeight = (_b = headerEl === null || headerEl === void 0 ? void 0 : headerEl.offsetHeight) !== null && _b !== void 0 ? _b : 34;
        // The editor toolbar is `position: fixed` and overlays the viewport's top.
        let toolbarBottomInViewport = 0;
        const toolbarEl = document.querySelector('.editor-toolbar');
        if (toolbarEl) {
            toolbarBottomInViewport = Math.max(0, toolbarEl.getBoundingClientRect().bottom - viewportRect.top);
        }
        const requested = keepBelowToolbar
            ? { x: pos.x, y: Math.max(pos.y, toolbarBottomInViewport) }
            : pos;
        const clamped = clampPanelPosition(requested, {
            viewportWidth: viewportRect.width,
            viewportHeight: viewportRect.height,
            panelWidth,
            headerHeight,
        });
        // If the clamped header still sits within the toolbar band the panel must
        // be raised above the toolbar to stay grabbable.
        return { clamped, overlapsToolbar: clamped.y < toolbarBottomInViewport };
    }, [_parentViewportRef, width]);
    // Apply a candidate position: clamp it into the window and refresh the
    // toolbar-overlap flag that drives z-index elevation. Returns the previous
    // position reference unchanged when nothing moved so React can skip the render.
    const applyViewportPosition = useCallback((pos, keepBelowToolbar = false) => {
        const { clamped, overlapsToolbar: overlaps } = clampAndMeasure(pos, keepBelowToolbar);
        setViewportPosition(prev => (prev.x === clamped.x && prev.y === clamped.y ? prev : clamped));
        setOverlapsToolbar(overlaps);
    }, [clampAndMeasure]);
    // Mirror the latest committed position into a ref. This runs as a layout
    // effect BEFORE the re-clamp effect below (layout effects fire in definition
    // order), so mount/unpin/resize always read the fresh value rather than one
    // commit behind — otherwise unpinning teleported the panel to the corner.
    useLayoutEffect(() => {
        viewportPositionRef.current = viewportPosition;
    });
    // On mount (and pin-state changes) drop the panel below the toolbar so default
    // positions are clean; the user can still drag it up into the top zone, where
    // it elevates to stay grabbable. Measuring the DOM and repositioning before
    // paint is the sanctioned use of useLayoutEffect + setState.
    useLayoutEffect(() => {
        if (isPinnedToCanvas)
            return;
        applyViewportPosition(viewportPositionRef.current, !initialAboveToolbar);
    }, [applyViewportPosition, isPinnedToCanvas, initialAboveToolbar]);
    // Keep the panel inside the window when the layout resizes, without shoving a
    // user-parked panel back out of the top zone (keepBelowToolbar stays false).
    useEffect(() => {
        const handleResize = () => {
            if (isPinnedToCanvas)
                return;
            applyViewportPosition(viewportPositionRef.current, false);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [applyViewportPosition, isPinnedToCanvas]);
    // --- Dragging Logic ---
    const handleHeaderMouseDown = (e) => {
        // Only proceed if draggable
        if (!isDraggable)
            return;
        const targetElement = e.target;
        const isPinButton = targetElement.closest('.pin-toggle-button');
        const isCloseButton = targetElement.closest('.panel-close-button');
        if (isPinButton || isCloseButton)
            return;
        const isDirectHeaderClick = e.currentTarget === targetElement ||
            targetElement.classList.contains('header-content') ||
            targetElement.tagName === 'SPAN';
        if (!isDirectHeaderClick)
            return;
        // Immediately update the current panel position to avoid a "jump"
        const currentScreenPos = getEffectiveScreenPosition();
        // First set the dragging state
        setIsDragging(true);
        // Then remember the initial cursor and panel position
        setDragStart({
            clientX: e.clientX,
            clientY: e.clientY,
            elementX: currentScreenPos.x,
            elementY: currentScreenPos.y
        });
        // Create a closure to store the current offset
        const dragOffsets = {
            clientX: e.clientX,
            clientY: e.clientY,
            elementX: currentScreenPos.x,
            elementY: currentScreenPos.y
        };
        const localHandleMouseMove = (moveEvent) => {
            moveEvent.preventDefault();
            // Use the closure with the saved initial values
            const deltaX = moveEvent.clientX - dragOffsets.clientX;
            const deltaY = moveEvent.clientY - dragOffsets.clientY;
            const newScreenX = dragOffsets.elementX + deltaX;
            const newScreenY = dragOffsets.elementY + deltaY;
            if (isPinnedToCanvas) {
                const newCanvasPos = viewportToCanvasCoords({ x: newScreenX, y: newScreenY }, zoomLevel, canvasTranslate);
                setCanvasPosition(newCanvasPos);
            }
            else {
                // Clamp into the window and keep the toolbar-overlap flag in sync so the
                // panel rides above the toolbar wherever the user parks it.
                applyViewportPosition({ x: newScreenX, y: newScreenY });
            }
        };
        const localHandleMouseUp = () => {
            setIsDragging(false);
            document.removeEventListener('mousemove', localHandleMouseMove);
            document.removeEventListener('mouseup', localHandleMouseUp);
        };
        document.addEventListener('mousemove', localHandleMouseMove);
        document.addEventListener('mouseup', localHandleMouseUp);
        e.preventDefault();
    };
    // --- Pinning Logic ---
    const handlePinToggle = (e) => {
        e.stopPropagation();
        setIsPinnedToCanvas(prevIsPinned => {
            const nextIsPinned = !prevIsPinned;
            const currentScreenPos = getEffectiveScreenPosition();
            if (nextIsPinned) {
                const newCanvasPos = viewportToCanvasCoords(currentScreenPos, zoomLevel, canvasTranslate);
                setCanvasPosition(newCanvasPos);
                setViewportPosition({ x: 0, y: 0 });
            }
            else {
                setViewportPosition(currentScreenPos);
                setCanvasPosition(null);
            }
            return nextIsPinned;
        });
    };
    // --- Close Logic ---
    const handleClose = (e) => {
        e.stopPropagation(); // Prevent drag start
        if (onClose) {
            onClose();
        }
    };
    // --- Style Calculation ---
    // No separate useEffect needed for zoom/translate changes, 
    // getEffectiveScreenPosition is recalculated on every render.
    // Raise the panel above the toolbar while dragging or when parked over it, so
    // its header is always grabbable; otherwise keep its normal (lower) stacking
    // so it never hides toolbar controls at rest.
    const elevate = shouldElevatePanel({ isPinnedToCanvas, isDragging, overlapsToolbar });
    const resolvedZIndex = elevate
        ? ELEVATED_PANEL_Z_INDEX
        : zIndex !== undefined ? zIndex : DEFAULT_PANEL_Z_INDEX;
    const panelStyle = Object.assign(Object.assign({ position: 'absolute', 
        // Use the calculated effective screen position
        left: `${effectiveScreenPosition.x}px`, top: `${effectiveScreenPosition.y}px`, 
        // Remove the counter-scaling transform. The panel will now scale with the canvasStage zoom.
        // transform: isPinnedToCanvas ? `scale(${1 / zoomLevel})` : 'none',
        transformOrigin: 'top left', 
        // Set cursor based on isDraggable and isDragging state
        cursor: isDraggable ? (isDragging ? 'grabbing' : 'grab') : 'default', zIndex: resolvedZIndex }, (width ? { width: typeof width === 'number' ? `${width}px` : width } : {})), (height ? { height: typeof height === 'number' ? `${height}px` : height } : {}));
    // Determine the container for the panel based on pin state
    // We need to render this component INSIDE the scrollable viewport when pinned to canvas,
    // and outside (e.g., as a direct child of the main app layout) when pinned to viewport.
    // This requires restructuring how/where FloatingPanel is rendered in LevelEditor.
    // For now, we assume it's always rendered in a context where absolute positioning works for both cases.
    // The 'transform' handles the visual positioning relative to the canvas content.
    return (_jsxs("div", { ref: panelRef, className: `floating-panel allow-native-interaction ${isPinnedToCanvas ? 'pinned-to-canvas' : 'pinned-to-viewport'} ${hideBackground ? 'no-background' : ''}`, style: Object.assign(Object.assign({}, panelStyle), (hideBackground ? { background: 'transparent', border: 'none', boxShadow: 'none' } : {})), children: [!hideHeader && (_jsx("div", { className: "panel-header", 
                // Conditionally attach mouse down handler and set cursor style
                onMouseDown: isDraggable ? handleHeaderMouseDown : undefined, style: { cursor: isDraggable ? (isDragging ? 'grabbing' : 'grab') : 'default' }, children: _jsxs("div", { className: "header-content", children: [_jsx("span", { children: title }), _jsxs("div", { className: "header-buttons", children: [showPinButton && (_jsx("button", { className: `pin-toggle-button ${isPinnedToCanvas ? 'pinned' : ''}`, onClick: handlePinToggle, title: isPinnedToCanvas ? "Unpin from Canvas" : "Pin to Canvas", children: _jsx(FaThumbtack, {}) })), onClose && (_jsx("button", { className: "panel-close-button", onClick: handleClose, title: "Close Panel", children: _jsx(FaTimes, {}) }))] })] }) })), hideHeader && showPinButton && (_jsx("button", { className: `pin-toggle-button floating-pin ${isPinnedToCanvas ? 'pinned' : ''}`, onClick: handlePinToggle, title: isPinnedToCanvas ? "Unpin from Canvas" : "Pin to Canvas", style: {
                    position: 'absolute',
                    top: '8px',
                    right: '8px',
                    zIndex: 1000,
                    background: 'rgba(0, 0, 0, 0.5)',
                    border: 'none',
                    borderRadius: '50%',
                    width: '32px',
                    height: '32px',
                    color: 'white',
                    cursor: 'pointer'
                }, children: _jsx(FaThumbtack, {}) })), hideHeader && onClose && (_jsx("button", { className: "panel-close-button floating-close", onClick: handleClose, title: "Close Panel", style: {
                    position: 'absolute',
                    top: '8px',
                    right: showPinButton ? '48px' : '8px',
                    zIndex: 1000,
                    background: 'rgba(0, 0, 0, 0.5)',
                    border: 'none',
                    borderRadius: '50%',
                    width: '32px',
                    height: '32px',
                    color: 'white',
                    cursor: 'pointer'
                }, children: _jsx(FaTimes, {}) })), _jsx("div", { className: "panel-content", style: Object.assign({}, (hideHeader ? { padding: 0 } : {})), children: children })] }));
};
export default FloatingPanel;
